import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { StarIcon } from './Icons';
import { Label } from './Label';

const labels: Record<number, string> = {
  0.5: 'Not Interested',
  1: 'Barely Interested',
  1.5: 'Somewhat Interested',
  2: 'Mild Interest',
  2.5: 'Neutral',
  3: 'Interested',
  3.5: 'Quite Interested',
  4: 'Very Interested',
  4.5: 'Highly Interested',
  5: 'Extremely Interested',
};

interface RatingProps {
  value?: number;
  onChange?: (value: number | null) => void;
  precision?: number;
  max?: number;
  readOnly?: boolean;
  disabled?: boolean;
  showLabel?: boolean;
  size?: 'sm' | 'default' | 'lg';
  className?: string;
  name?: string;
  label?: string;
  required?: boolean;
  error?: string;
  helperText?: string;
}

/**
 * Rating - Enhanced star rating component with hover labels and half-star support
 * Supports half-star precision, hover feedback, and smooth animations
 */
export const Rating: React.FC<RatingProps> = ({
  value = 0,
  onChange,
  precision = 0.5,
  max = 5,
  readOnly = false,
  disabled = false,
  showLabel = true,
  size = 'default',
  className = '',
  name,
  label,
  required = false,
  error,
  helperText,
}) => {
  const { colors } = useTheme();
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  const [displayValue, setDisplayValue] = useState<number>(value || 0);
  const starRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    setDisplayValue(value || 0);
  }, [value]);

  const sizeClasses = {
    sm: 'w-4 h-4',
    default: 'w-6 h-6',
    lg: 'w-8 h-8',
  };

  const handleStarClick = (starIndex: number, isHalf: boolean) => {
    if (readOnly || disabled) return;
    
    const newValue = isHalf ? starIndex - 0.5 : starIndex;
    
    // Toggle off if clicking the same value
    const finalValue = displayValue === newValue ? 0 : newValue;
    
    setDisplayValue(finalValue);
    onChange?.(finalValue || null);
  };

  const handleStarHover = (starIndex: number | null, isHalf: boolean = false) => {
    if (readOnly || disabled) return;
    if (starIndex === null) {
      setHoverValue(null);
      return;
    }
    setHoverValue(isHalf ? starIndex - 0.5 : starIndex);
  };

  const getStarFill = (index: number): 'full' | 'half' | 'empty' => {
    const currentValue = hoverValue !== null ? hoverValue : displayValue;
    const starIndex = index + 1;
    
    if (currentValue >= starIndex) {
      return 'full';
    } else if (precision === 0.5 && currentValue >= starIndex - 0.5) {
      return 'half';
    }
    return 'empty';
  };

  const currentLabel = hoverValue !== null 
    ? labels[hoverValue] || `${hoverValue} Star${hoverValue !== 1 ? 's' : ''}`
    : labels[displayValue] || (displayValue > 0 ? `${displayValue} Star${displayValue !== 1 ? 's' : ''}` : 'No Rating');

  const renderStar = (index: number) => {
    const starIndex = index + 1;
    const fill = getStarFill(index);
    const isActive = fill === 'full' || fill === 'half';
    
    return (
      <div
        key={index}
        ref={(el) => (starRefs.current[index] = el)}
        className="relative inline-block"
        onMouseLeave={() => handleStarHover(null, false)}
      >
        {precision === 0.5 ? (
          // Half-star precision: split star into left and right halves
          <div className="relative">
            {/* Background empty star */}
            <StarIcon
              className={cn(
                sizeClasses[size],
                colors.textMuted,
                'opacity-40'
              )}
              filled={false}
            />
            
            {/* Left half (for half-star) */}
            <div
              className="absolute inset-0 overflow-hidden"
              style={{ width: '50%' }}
              onMouseEnter={() => handleStarHover(starIndex, true)}
              onClick={() => handleStarClick(starIndex, true)}
            >
              <StarIcon
                className={cn(
                  sizeClasses[size],
                  fill === 'half' || fill === 'full'
                    ? colors.warning
                    : colors.textMuted,
                  !readOnly && !disabled && 'cursor-pointer transition-all duration-150',
                  !readOnly && !disabled && (fill === 'half' || fill === 'full') && 'hover:scale-110',
                  readOnly && 'cursor-default',
                  disabled && 'opacity-50 cursor-not-allowed'
                )}
                filled={fill === 'half' || fill === 'full'}
              />
            </div>
            
            {/* Right half (for full star) - only show if full */}
            {fill === 'full' && (
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ left: '50%', width: '50%' }}
                onMouseEnter={() => handleStarHover(starIndex, false)}
                onClick={() => handleStarClick(starIndex, false)}
              >
                <StarIcon
                  className={cn(
                    sizeClasses[size],
                    colors.warning,
                    !readOnly && !disabled && 'cursor-pointer transition-all duration-150 hover:scale-110',
                    readOnly && 'cursor-default',
                    disabled && 'opacity-50 cursor-not-allowed'
                  )}
                  filled={true}
                />
              </div>
            )}
          </div>
        ) : (
          // Integer precision: whole stars only
          <div
            onClick={() => handleStarClick(starIndex, false)}
            onMouseEnter={() => handleStarHover(starIndex, false)}
            className={cn(
              'transition-all duration-150',
              !readOnly && !disabled && 'cursor-pointer hover:scale-110',
              readOnly && 'cursor-default',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <StarIcon
              className={cn(
                sizeClasses[size],
                fill === 'full'
                  ? colors.warning
                  : colors.textMuted,
                'transition-colors duration-150'
              )}
              filled={fill === 'full'}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <Label htmlFor={name} required={required}>
          {label}
        </Label>
      )}
      <div
        className={cn(
          'relative border rounded-lg p-3 transition-colors',
          colors.borderLight,
          colors.surface,
          error ? 'border-red-500' : '',
          !disabled && !readOnly && colors.interactiveHover,
          disabled || readOnly ? 'opacity-50' : ''
        )}
      >
        {/* Legend label */}
        {!label && (
          <div
            className={cn(
              'absolute -top-2.5 left-3 px-2 text-sm font-medium rounded',
              colors.surface,
              colors.text,
              'text-xs'
            )}
          >
            Interest Rating
          </div>
        )}
        
        {/* Stars and label */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            {Array.from({ length: max }).map((_, index) => renderStar(index))}
          </div>
          
          {showLabel && (
            <span
              className={cn(
                'text-sm font-medium transition-colors duration-200 min-w-[140px]',
                hoverValue !== null
                  ? colors.text
                  : colors.textMuted
              )}
            >
              {currentLabel}
            </span>
          )}
        </div>
      </div>
      
      {/* Error or helper text */}
      {(error || helperText) && (
        <div className={cn('mt-1.5 text-xs', error ? colors.error : colors.textMuted)}>
          {error || helperText}
        </div>
      )}
      
      {/* Hidden input for form compatibility */}
      {name && (
        <input
          type="hidden"
          name={name}
          value={displayValue}
        />
      )}
    </div>
  );
};

/**
 * RatingView - Read-only rating display component
 * Used for displaying ratings in tables and detail views
 */
interface RatingViewProps {
  value?: number;
  max?: number;
  precision?: number;
  size?: 'sm' | 'default' | 'lg';
  showText?: boolean;
  className?: string;
}

export const RatingView: React.FC<RatingViewProps> = ({
  value = 0,
  max = 5,
  precision = 0.5,
  size = 'default',
  showText = false,
  className = '',
}) => {
  const { colors } = useTheme();
  const ratingValue = value || 0;
  const labelText = labels[ratingValue] || 'No Rating';

  const sizeClasses = {
    sm: 'w-3 h-3',
    default: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  const getStarFill = (index: number): 'full' | 'half' | 'empty' => {
    const starIndex = index + 1;
    
    if (ratingValue >= starIndex) {
      return 'full';
    } else if (precision === 0.5 && ratingValue >= starIndex - 0.5) {
      return 'half';
    }
    return 'empty';
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div className="flex items-center gap-0.5">
        {Array.from({ length: max }).map((_, index) => {
          const fill = getStarFill(index);
          
          return precision === 0.5 && fill === 'half' ? (
            <div key={index} className="relative">
              <StarIcon
                className={cn(sizeClasses[size], colors.textMuted, 'opacity-40')}
                filled={false}
              />
              <div className="overflow-hidden absolute inset-0" style={{ width: '50%' }}>
                <StarIcon
                  className={cn(sizeClasses[size], colors.warning)}
                  filled={true}
                />
              </div>
            </div>
          ) : (
            <StarIcon
              key={index}
              className={cn(
                sizeClasses[size],
                fill === 'full' ? colors.warning : colors.textMuted,
                fill === 'full' ? '' : 'opacity-40'
              )}
              filled={fill === 'full'}
            />
          );
        })}
      </div>
      
      {showText && (
        <span className={cn('text-xs font-medium', colors.textMuted)}>
          {labelText}
        </span>
      )}
    </div>
  );
};

export default Rating;
